import React, { useEffect, useState } from "react";
import { api, fileToBase64, formatMXN } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent } from "./ui/dialog";
import { CreditCard, Upload, CheckCircle2, Clock, XCircle, Copy, AlertTriangle, SplitSquareHorizontal, PartyPopper } from "lucide-react";
import { toast } from "sonner";

export default function PaymentFlow({ business, onSubmitted }) {
  const [payments, setPayments] = useState([]);
  const [instructions, setInstructions] = useState(null);
  const [payMode, setPayMode] = useState("split"); // "split" | "iva"
  const [reference, setReference] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [successData, setSuccessData] = useState(null); // { expires_at }

  const load = async () => {
    try {
      const [paymentsRes, instrRes] = await Promise.all([
        api.get("/payments/me"),
        api.get("/business/payment-instructions"),
      ]);
      setPayments(paymentsRes.data);
      setInstructions(instrRes.data);
    } catch (err) {
      console.warn("PaymentFlow: load error", err?.response?.data || err?.message);
    }
  };

  useEffect(() => { load(); }, []);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado");
  };

  const getAmount = () => {
    if (!instructions) return 199;
    const { amounts, promoter } = instructions;
    if (promoter?.payment_account) {
      return payMode === "iva" ? amounts.with_iva : amounts.full;
    }
    return amounts.full;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Sube tu comprobante");
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.post("/payments/submit", {
        receipt_base64: b64,
        reference,
        amount: getAmount(),
      });
      setReference(""); setFile(null);
      await load();
      onSubmitted && onSubmitted();
      setSuccessData({ expires_at: data.applied_expires_at });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar el comprobante");
    } finally {
      setBusy(false);
    }
  };

  const subStatus = business?.subscription_active;
  const hasPromoter = instructions?.promoter != null;
  const hasPromoterAccount = Boolean(instructions?.promoter?.payment_account);
  const { amounts, owner_bank, company_bank, promoter } = instructions || {};

  return (
    <>
    <div className="grid md:grid-cols-5 gap-6">
      <div className="md:col-span-3 space-y-6">
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-5 h-5 text-[hsl(var(--primary))]" />
            <h3 className="font-display text-2xl tracking-tight">Pago mensual</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            El costo es de <strong>{formatMXN(199)} MXN al mes</strong>. Aceptamos exclusivamente transferencias bancarias.
          </p>

          {/* Promoter with no account registered yet */}
          {hasPromoter && !hasPromoterAccount && (
            <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Tu promotor <strong>{promoter?.name}</strong> aún no ha registrado su cuenta de cobro.
                Realiza el pago completo de <strong>{formatMXN(199)}</strong> a la cuenta de abajo por ahora.
              </span>
            </div>
          )}

          {/* Mode selector — only when promoter has account */}
          {hasPromoterAccount && (
            <div className="mt-5 flex gap-3 flex-wrap">
              <button
                onClick={() => setPayMode("split")}
                className={`flex-1 min-w-[140px] rounded-xl border-2 px-4 py-3 text-sm font-medium transition-colors ${
                  payMode === "split"
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 text-[hsl(var(--primary))]"
                    : "border-border text-muted-foreground hover:border-[hsl(var(--primary))]/50"
                }`}
                data-testid="pay-mode-split"
              >
                <SplitSquareHorizontal className="w-4 h-4 inline mr-1.5" />
                Sin factura · {formatMXN(amounts?.full || 199)}
              </button>
              <button
                onClick={() => setPayMode("iva")}
                className={`flex-1 min-w-[140px] rounded-xl border-2 px-4 py-3 text-sm font-medium transition-colors ${
                  payMode === "iva"
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 text-[hsl(var(--primary))]"
                    : "border-border text-muted-foreground hover:border-[hsl(var(--primary))]/50"
                }`}
                data-testid="pay-mode-iva"
              >
                Con factura (IVA) · {formatMXN(amounts?.with_iva || 230.84)}
              </button>
            </div>
          )}

          {/* Payment instructions */}
          {hasPromoterAccount && payMode === "split" ? (
            <div className="mt-5 space-y-4">
              <div className="rounded-xl bg-[#FAFAFA] border border-border p-5 space-y-3">
                <div className="overline text-muted-foreground">
                  Cuenta 1 · Deposita <strong>{formatMXN(amounts?.split_owner || 99)}</strong>
                </div>
                <Row label="Beneficiario" value={owner_bank?.beneficiary} onCopy={copy} />
                <Row label="Banco" value={owner_bank?.bank} onCopy={copy} />
                <Row label="Cuenta" value={owner_bank?.account} onCopy={copy} mono />
                <Row label="Monto" value={formatMXN(amounts?.split_owner || 99)} onCopy={copy} />
              </div>

              <div className="rounded-xl bg-[#F5F3FF] border border-[#DDD6FE] p-5 space-y-3">
                <div className="overline text-[#5B21B6]">
                  Cuenta 2 · Deposita <strong>{formatMXN(amounts?.split_promoter || 100)}</strong> · Tu promotor: {promoter?.name}
                </div>
                <Row label="Beneficiario" value={promoter.payment_account.account_holder} onCopy={copy} />
                <Row label="Banco" value={promoter.payment_account.bank_name} onCopy={copy} />
                <Row label="Cuenta" value={promoter.payment_account.account_number} onCopy={copy} mono />
                <Row label="Monto" value={formatMXN(amounts?.split_promoter || 100)} onCopy={copy} />
              </div>

              <div className="text-xs text-muted-foreground px-1">
                Sube <strong>un solo comprobante</strong> por el depósito que quieras (o el del promotor). El admin registrará ambos.
              </div>
            </div>
          ) : hasPromoterAccount && payMode === "iva" ? (
            <div className="mt-5 rounded-xl bg-[#FAFAFA] border border-border p-5 space-y-3">
              <div className="overline text-muted-foreground">
                Datos para transferencia con factura · <strong>{formatMXN(amounts?.with_iva || 230.84)}</strong>
              </div>
              <Row label="Beneficiario" value={company_bank?.beneficiary} onCopy={copy} />
              <Row label="Banco" value={company_bank?.bank} onCopy={copy} />
              <Row label="Cuenta" value={company_bank?.account} onCopy={copy} mono />
              <Row label="Monto" value={formatMXN(amounts?.with_iva || 230.84)} onCopy={copy} />
              <div className="text-xs text-muted-foreground pt-1">
                Envía tus datos de facturación por WhatsApp tras realizar el depósito.
              </div>
            </div>
          ) : (
            /* No promoter or promoter without account: standard single payment */
            <div className="mt-5 rounded-xl bg-[#FAFAFA] border border-border p-5 space-y-3">
              <div className="overline text-muted-foreground">Datos para transferencia</div>
              <Row label="Beneficiario" value={owner_bank?.beneficiary || "RICARDO ZERMEÑO MEJIA"} onCopy={copy} />
              <Row label="Banco" value={owner_bank?.bank || "BBVA"} onCopy={copy} />
              <Row label="Cuenta" value={owner_bank?.account || "012 320 004618064241"} onCopy={copy} mono />
              <Row label="Monto" value={formatMXN(199)} onCopy={copy} />
              <Row label="Concepto sugerido" value={`OFERTAS ${business?.business_name || ""}`} onCopy={copy} />
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="payment-form">
            <div>
              <Label className="text-sm">Referencia / folio de la transferencia (opcional)</Label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="mt-1.5"
                placeholder="Ej. SPEI 12345..."
                data-testid="payment-ref-input"
              />
            </div>
            <div>
              <Label className="text-sm">Comprobante (imagen o PDF) *</Label>
              <label className="mt-1.5 flex items-center gap-3 border border-dashed border-input rounded-md p-4 cursor-pointer hover:bg-muted/50">
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground flex-1">
                  {file ? file.name : "Da clic para subir tu comprobante"}
                </span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="payment-file-input"
                />
              </label>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full btn-primary rounded-full py-6"
              data-testid="payment-submit-btn"
            >
              {busy ? "Enviando…" : "Enviar comprobante"}
            </Button>
          </form>
        </div>
      </div>

      <div className="md:col-span-2 space-y-6">
        <div className={`rounded-2xl p-6 border ${subStatus ? "bg-[#EDE9FE] border-[#7C3AED]/30" : "bg-[#F4F4F5] border-zinc-300"}`}>
          <div className="overline text-muted-foreground">Estado de tu suscripción</div>
          {subStatus ? (
            <>
              <div className="font-display text-2xl mt-2 text-[#5B21B6]">Activa ✓</div>
              <div className="text-sm text-muted-foreground mt-1">
                Vence: {new Date(business.subscription_expires_at).toLocaleDateString("es-MX")}
              </div>
            </>
          ) : (
            <>
              <div className="font-display text-2xl mt-2 text-zinc-900">Inactiva</div>
              <div className="text-sm text-muted-foreground mt-1">
                Realiza el pago y sube tu comprobante para activarte.
              </div>
            </>
          )}
        </div>

        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="overline text-muted-foreground">Historial de pagos</div>
          {payments.length === 0 ? (
            <div className="text-sm text-muted-foreground mt-3">Aún no has enviado comprobantes.</div>
          ) : (
            <ul className="mt-3 space-y-3">
              {payments.map((p) => (
                <li key={p.id} className="border border-border rounded-lg p-3" data-testid={`payment-row-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">{formatMXN(p.amount)}</div>
                    <StatusBadge status={p.status} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {new Date(p.created_at).toLocaleString("es-MX")}
                    {p.reference ? ` · Ref: ${p.reference}` : ""}
                  </div>
                  {p.notes ? <div className="text-xs text-muted-foreground mt-1">Nota: {p.notes}</div> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>

    <Dialog open={!!successData} onOpenChange={(o) => !o && setSuccessData(null)}>
      <DialogContent className="max-w-sm text-center" data-testid="payment-success-dialog">
        <div className="flex flex-col items-center gap-4 py-4">
          <div className="w-20 h-20 rounded-full bg-[#E05A47]/10 flex items-center justify-center">
            <PartyPopper className="w-10 h-10 text-[#E05A47]" />
          </div>
          <div>
            <h2 className="font-display text-2xl tracking-tight">¡Gracias por tu pago!</h2>
            <p className="text-muted-foreground text-sm mt-2">
              Recibimos tu comprobante. Tu suscripción ya está <strong>activa</strong>.
            </p>
            {successData?.expires_at && (
              <p className="text-sm mt-1 text-[hsl(var(--primary))] font-medium">
                Vence el {new Date(successData.expires_at).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            )}
          </div>
          <p className="text-xs text-muted-foreground px-4">
            Ya puedes generar tu anuncio del mes. Verificaremos tu comprobante en las próximas horas.
          </p>
          <Button className="btn-primary rounded-full px-8" onClick={() => setSuccessData(null)} data-testid="payment-success-close">
            ¡Entendido!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}


const Row = ({ label, value, onCopy, mono }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="text-xs text-muted-foreground w-32">{label}</div>
    <div className={`flex-1 text-sm ${mono ? "font-mono" : "font-medium"}`}>{value}</div>
    <button onClick={() => onCopy(value)} className="p-1 rounded hover:bg-muted" title="Copiar">
      <Copy className="w-3.5 h-3.5 text-muted-foreground" />
    </button>
  </div>
);

const StatusBadge = ({ status }) => {
  if (status === "approved") return <Badge className="bg-[#7C3AED] text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Activo</Badge>;
  if (status === "rejected") return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rechazado</Badge>;
  if (status === "cancelled") return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Cancelado</Badge>;
  return <Badge className="bg-zinc-200 text-zinc-900"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
};
